import {
  GROUP_MAP,
  TYPE_MAP,
  TOP_N_RANKING,
} from "./RankedInterventionSelectorConstants";

import { wcpmPivot } from "../../constants";

export class InterventionItem {
  constructor(codename, rank) {
    this.codename = codename;
    this.rank = rank;
  }
}

export class RankedInterventionSelector {
  constructor() {
    // This object stores the breakdown of intervention items based on TYPE_MAP and GROUP_MAP. It organizes intervention items into nested arrays corresponding to different types and groups.
    this.typeBreakdown = {};

    // Tracks our current state of available interventions.
    this.interventionTracking = {};

    //Provided function that checks whether an intervention is available for a given word.
    this.isInterventionSupported = () => true;

    this.initializeTypeBreakdown(); // NOTE: We must initialize the type breakdown first before we can initialize the global tracking
    this.initializeInterventionTracking(); // NOTE: This must come after initializeTypeBreakdown()
  }

  initializeInterventionTracking() {
    this.interventionTracking = this.randomizeOptions(
      this.rankTransform(this.typeBreakdown)
    );
  }

  initializeTypeBreakdown() {
    // Loop through each key-value pair in TYPE_MAP
    for (const typeKey in TYPE_MAP) {
      const typeValue = TYPE_MAP[typeKey];

      // Create an array to store intervention items for the current type
      const typeInterventionItems = [];

      // Loop through each value (sub-array) in the typeValue array
      for (let i = 0; i < typeValue.length; i++) {
        const groupKey = typeValue[i];
        const groupInterventionItems = [];

        // Loop through each intervention item in the GROUP_MAP using the groupKey
        for (const itemKey of GROUP_MAP[groupKey]) {
          const interventionItem = new InterventionItem(itemKey, i);
          groupInterventionItems.push(interventionItem);
        }

        typeInterventionItems.push(groupInterventionItems);
      }

      this.typeBreakdown[typeKey] = typeInterventionItems;
    }
  }

  setIsInterventionSupported(isInterventionSupportedFunc) {
    this.isInterventionSupported = isInterventionSupportedFunc;
  }

  /**
   * Randomizes the order of options using a shuffling approach.
   * @param {Object|Array} input - The input data containing options to be randomized.
   * @returns {Object|Array} The input data with randomized options.
   */
  randomizeOptions(input) {
    const randomizeArray = (inputArray) => {
      // Create a copy of the input array to avoid modifying the original
      const newArray = [...inputArray];

      // Use the Fisher-Yates (Knuth) shuffle algorithm to shuffle the array
      for (let i = newArray.length - 1; i > 0; i--) {
        const randomIndex = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[randomIndex]] = [
          newArray[randomIndex],
          newArray[i],
        ];
      }

      return newArray;
    };

    const randomizeArraysInArray = (arrays) => {
      return arrays.map(randomizeArray);
    };

    if (Array.isArray(input)) {
      return randomizeArraysInArray(input);
    }

    if (typeof input === "object" && input !== null) {
      const output = {};
      for (const [k, v] of Object.entries(input)) {
        output[k] = randomizeArraysInArray(v);
      }
      return output;
    }

    return input;
  }

  /**
   * Generates a random index based on an array of probabilities.
   * @param {number[]} probabilities - An array of probabilities representing weights for each index.
   * @returns {number} The randomly chosen index based on the probabilities.
   */
  weightedRandomIndex(probabilities) {
    const randomValue = Math.random();
    let cumulativeProbability = 0;
    let maxIndex = 0; // Keep track of the index with the highest weight
    let maxWeight = probabilities[0]; // Initialize with the first weight

    for (let i = 0; i < probabilities.length; i++) {
      // Update maxIndex if the current weight is greater than the maxWeight found so far
      if (probabilities[i] > maxWeight) {
        maxWeight = probabilities[i];
        maxIndex = i;
      }

      cumulativeProbability += probabilities[i];
      if (randomValue <= cumulativeProbability) {
        return i;
      }
    }

    // If the function hasn't returned by now, return the index of the highest weight
    return maxIndex;
  }

  /**
   * Rearranges a resampled list to return a list of lists with items of the same rank.
   * @param {Array} resampled - The resampled list.
   * @returns {Array<Array>} The rearranged list of lists.
   */
  rearrangeOutput(resampled) {
    const rearranged = [[], [], [], []];

    for (const i of resampled) {
      rearranged[i.rank].push(i);
    }

    return rearranged.filter((array) => array.length > 0);
  }

  /**
   * Transforms the input by rearranging items based on ranks and sampling weights.
   * @param {Object} input - The input data containing lists of items and their ranks.
   * @returns {Object} The transformed output with rearranged items.
   */
  rankTransform(input) {
    /*
        Example of input:
        const input = {
            'B1': [
                [
                    { codename: 'elkoninbox1', rank: 0 }, // InterventionItem class
                    { codename: 'elkoninbox2', rank: 0 },
                    { codename: 'elkoninbox4', rank: 0 },
                    { codename: 'phonemegiveword', rank: 0 }
                ],
                [
                    { codename: 'wordinvestigator1', rank: 1 },
                    { codename: 'wordinvestigator2', rank: 1 }
                ],
                [
                    { codename: 'simpledefinition', rank: 2 },
                    { codename: 'vocabularywithpictures', rank: 2 },
                    { codename: 'funfact', rank: 2 }
                ]
            ],
            ... other categories and their items
        };
    */

    const output = {};

    for (const [k, v] of Object.entries(input)) {
      const sampleBases = [];
      const weightBases = [];

      for (const [index, interventionItemArray] of v.entries()) {
        const items = [];
        const counts = [];

        const occurrences = {};
        for (const interventionItem of interventionItemArray) {
          const { codename } = interventionItem;
          occurrences[codename] = (occurrences[codename] || 0) + 1;
        }

        for (const interventionItem of interventionItemArray) {
          const { codename } = interventionItem;
          if (occurrences[codename]) {
            items.push(interventionItem);
            counts.push(occurrences[codename]);
            delete occurrences[codename]; // Prevent us from looking at this one again
          }
        }

        const weights = counts.map(
          (count) =>
            (count / counts.reduce((a, b) => a + b)) *
            (TOP_N_RANKING[k][index] / TOP_N_RANKING[k].reduce((a, b) => a + b))
        );

        sampleBases.push(items);
        weightBases.push(weights);
      }

      const sampleMerged = sampleBases.flat();
      const weightMerged = weightBases.flat();
      const resampled = [];

      for (let i = 0; i < sampleMerged.length; i++) {
        const choice = this.weightedRandomIndex(weightMerged);
        resampled.push(sampleMerged[choice]);
      }

      output[k] = this.rearrangeOutput(resampled);
    }

    return output;
  }

  /**
   * Selects the appropriate intervention group based on wordInterventionMetadata, WCPM, and whether or not we are in an ERSS activity.
   * @returns {string} Intervention group to be supplied to drawIntervention, e.g. 'A', 'A01', 'B01', etc.
   */
  selectInterventionGroup(wordInterventionMetadata, wcpm, isEarlyReader) {
    if (isEarlyReader) {
      return wordInterventionMetadata.ERSS_default_ranked;
    }

    if (wcpm < wcpmPivot) {
      return wordInterventionMetadata.low_default_ranked;
    } else {
      return wordInterventionMetadata.high_default_ranked;
    }
  }

  /**
   * Draws an intervention item based on the specified intervention type.
   * @param {string} type - The type of the intervention ranklist, e.g. 'A', 'A01', 'B01', etc.
   * @returns {Object|null} The drawn intervention item or null if not available.
   */
  drawIntervention(type) {
    if (!this.typeBreakdown[type]) {
      // Invalid type
      return null;
    }

    // If there are no more available interventions of the specified type,
    // replenish the options by randomizing the ranklist for that type.
    if (
      !this.interventionTracking[type] ||
      this.interventionTracking[type].length === 0
    ) {
      this.interventionTracking[type] = this.randomizeOptions(
        this.rankTransform(this.typeBreakdown)[type]
      );
    }

    const typeRecord = [...this.interventionTracking[type]];

    let selectedRankIndex;
    let selectedInterventionIndex;
    for (let rankIndex = 0; rankIndex < typeRecord.length; rankIndex++) {
      const rankArray = typeRecord[rankIndex];
      for (
        let interventionIndex = 0;
        interventionIndex < rankArray.length;
        interventionIndex++
      ) {
        const interventionItem = rankArray[interventionIndex];
        if (this.isInterventionSupported(interventionItem.codename)) {
          // Intervention is supported. Stop loop.
          selectedRankIndex = rankIndex;
          selectedInterventionIndex = interventionIndex;
          break;
        }
      }

      if (
        selectedRankIndex !== undefined &&
        selectedInterventionIndex !== undefined
      ) {
        // Intervention was found, no need to keep looping.
        break;
      }
    }

    if (
      selectedRankIndex === undefined ||
      selectedInterventionIndex === undefined
    ) {
      // No suitable intervention found for the provided type. Attempt to use fallback.
      return this.drawInterventionFallback(type);
    }

    // Remove the selected intervention item from the selected rank array
    const selectedInterventionItem = typeRecord[selectedRankIndex].splice(
      selectedInterventionIndex,
      1
    )[0];

    // Remove the selected rank array from the type record if it is empty
    if (typeRecord[selectedRankIndex].length === 0) {
      typeRecord.splice(selectedRankIndex, 1);
    }

    this.interventionTracking[type] = typeRecord; // Update interventionTracking with the new type record
    return selectedInterventionItem;
  }

  /**
   * Selects intervention from typeBreakdown as fallback
   * @param {string} type - The type of the intervention ranklist, e.g. 'A', 'A01', 'B01', etc.
   * @returns {Object|null} The drawn intervention item or null if not available.
   */
  drawInterventionFallback(type) {
    if (!this.typeBreakdown[type]) {
      // Invalid type
      return null;
    }

    const availableInterventions = this.typeBreakdown[type].flat();
    for (const interventionItem of availableInterventions) {
      if (this.isInterventionSupported(interventionItem.codename)) {
        return interventionItem;
      }
    }

    // No supported intervention found
    return null;
  }

  /**
   * ======================
   * ========= V2 =========
   * ======================
   */

  /**
   * Generates a randomized ordering of keys from a weighted item object.
   * Items are selected based on their weights, ensuring no repetition.
   * The function adjusts weights after each selection to maintain probability distribution.
   *
   * @param {Object} weightedItems - Object with items as keys and their weights as values.
   * @returns {Array} Ordered array of keys, based on weighted random selection.
   */
  generateOrdering(weightedItems) {
    /**
     * Example Usage:
     * let weightedItems = {
     *   "funfact": 0.5,
     *   "elkonin1": 0.3,
     *   "elkonin2": 0.2
     * };
     * let orderedKeys = generateOrdering(inputWeights);
     * console.log(orderedKeys); // Might output: ["elkonin1", "funfact", "elkonin2"]
     */

    let ordering = [];
    let items = Object.keys(weightedItems); // Extracting item keys
    let weights = Object.values(weightedItems); // Extracting weights

    let sumOfWeights = weights.reduce((a, b) => a + b, 0);

    while (sumOfWeights > 0) {
      let selectedIndex = this.weightedRandomIndex(weights);
      ordering.push(items[selectedIndex]); // Push the selected item key

      weights[selectedIndex] = 0.0; // Set the selected item's weight to 0
      sumOfWeights = weights.reduce((a, b) => a + b, 0); // Recalculate the sum of weights

      if (sumOfWeights > 0) {
        // Normalize the remaining weights
        weights = weights.map((weight) =>
          weight > 0 ? weight / sumOfWeights : 0
        );
      }
    }

    return ordering;
  }

  /**
   * V2: Draws an intervention item based on supplies intervention weights.
   * @param {Object} weightedItems - Object with items as keys and their weights as values.
   * @returns {Object|null} The drawn intervention item or null if not available.
   */
  drawInterventionV2(weightedItems) {
    const ordering = this.generateOrdering(weightedItems);

    for (const intervention of ordering) {
      if (this.isInterventionSupported(intervention)) {
        return intervention;
      }
    }

    return null;
  }
}
